import React, { useState, useEffect } from 'react'
import { BoltIcon, BuildingOffice2Icon, GiftIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { IncentivePlan, IncentivePlans, IncentivePlanOptionsResult, BillingSdkTenantAccountBalances } from '../../../../apis/types'
import { getTenantAccountBalancesFromBillingSdkApi } from '../../../../apis/apis'
import Panel from '../../../../components/Panel'
import Badge from '../../../../components/Badge'
import Button from '../../../../components/Form/Button'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import { formatDateTime, formatUTCDetailedDate } from '../../../../util/string'

import CreatePaymentPlanForm from './billing/CreatePaymentPlanForm'
import CreateUtilityPaymentPlanForm from './billing/CreateUtilityPaymentPlanForm'
import PaymentPlanDetail from './billing/PaymentPlanDetail'
import CreateCreditForm from './billing/CreateCreditForm'
import CreditDetail from './billing/CreditDetail'

// ---------------------------------------------------------------------------
// Mini-router types
// ---------------------------------------------------------------------------
type BillingView =
    | { view: 'overview' }
    | { view: 'create-payment-plan' }
    | { view: 'create-utility-payment-plan' }
    | { view: 'payment-plan-detail'; planId: string; accountType: 'King' | 'Utility' }
    | { view: 'create-credit' }
    | { view: 'credit-detail'; creditId: string; accountType: 'King' | 'Utility' }

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface TabIncentivePlansProps {
    title: string
    /** Optional count shown in the tab badge (e.g. active incentives count) */
    badge?: number | string
    data: IncentivePlans
    tenantAccountId: string
    incentivePlanOptions: IncentivePlanOptionsResult | undefined
    lastKingInvoice?: { billingStartDate?: string; billingEndDate?: string; dueDate?: string }
    utilityName?: string
    /** Called after create/edit/cancel/reverse so the parent can refetch and show latest data */
    onRefresh?: () => Promise<void>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0)
}

const formatDate = (dateStr: string) =>
    formatUTCDetailedDate(new Date(dateStr))

const PAYMENT_PLAN_TYPES = ['PAYMENT_PLAN']
const CREDIT_TYPES = ['ACCOMMODATION_CREDIT']

function isPlanType(plan: IncentivePlan): boolean {
    return PAYMENT_PLAN_TYPES.includes(plan.type)
}

function isCreditType(plan: IncentivePlan): boolean {
    return CREDIT_TYPES.includes(plan.type)
}

// ---------------------------------------------------------------------------
// Overview sub-components
// ---------------------------------------------------------------------------

interface PlanTableProps {
    plans: IncentivePlan[]
    accountType: 'King' | 'Utility'
    onRowClick: (plan: IncentivePlan) => void
    /** Rendered right-aligned in the footer (e.g. Add plan button) */
    footerAction?: React.ReactNode
    /** Label used in the empty-state message, e.g. "SCE" → "No active SCE payment plans" */
    emptyLabel?: string
}

const PlanTable: React.FC<PlanTableProps> = ({ plans, accountType: _accountType, onRowClick, footerAction, emptyLabel }) => {
    const [showInactive, setShowInactive] = useState(false)
    const activePlans = plans.filter((p) => p.status === 'ACTIVE')
    const inactivePlans = plans.filter((p) => p.status !== 'ACTIVE')
    const visiblePlans = showInactive ? plans : activePlans

    if (plans.length === 0) {
        return (
            <div>
                <div className="py-2.5">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        No active {emptyLabel ?? 'payment'} payment plans
                    </p>
                </div>
                {footerAction != null && (
                    <div className="flex items-center justify-end py-3 dark:bg-slate-800">
                        {footerAction}
                    </div>
                )}
            </div>
        )
    }

    const showFooter = inactivePlans.length > 0 || footerAction != null

    return (
        <div>
            {visiblePlans.length === 0 && (
                <div className="py-2.5">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No active {emptyLabel ?? ''} payment plans</p>
                </div>
            )}
            <div className={visiblePlans.length === 0 ? 'hidden' : 'overflow-x-auto'}>
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead className="bg-white dark:bg-slate-950">
                        <tr>
                            {['Plan ID', 'Status', 'Installments', 'Starting Balance', 'Start Date', 'End Date'].map((h) => (
                                <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider dark:text-white">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {visiblePlans.map((plan) => {
                            const paidCount = plan.installments.filter((i) => i.status === 'PAID' || i.status === 'BILLED').length
                            const progressPercent =
                                plan.installmentCount > 0 ? Math.round((paidCount / plan.installmentCount) * 100) : 0
                            const lastInstallmentDueDate =
                                plan.installments.length > 0
                                    ? plan.installments[plan.installments.length - 1].dueDate
                                    : null
                            const endDateDisplay =
                                plan.status === 'CANCELLED'
                                    ? (plan.cancelledAt ? formatDate(plan.cancelledAt) : (plan.endDate ? formatDate(plan.endDate) : '—'))
                                    : plan.status === 'ACTIVE'
                                        ? (lastInstallmentDueDate ? `Est. ${formatDate(lastInstallmentDueDate)}` : (plan.endDate ? formatDate(plan.endDate) : '—'))
                                        : (plan.endDate ? formatDate(plan.endDate) : '—')
                            return (
                                <tr
                                    key={plan.id}
                                    className="bg-slate-50 dark:bg-slate-950/50 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                                    onClick={() => onRowClick(plan)}
                                >
                                    <td className="px-4 py-3 text-sm font-mono dark:text-gray-300">{plan.humanId ?? plan.id}</td>
                                    <td className="px-4 py-3">
                                        <Badge status={plan.status} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="min-w-[4rem] space-y-1">
                                            <div className="h-1.5 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-green-500 dark:bg-green-400 transition-all"
                                                    style={{ width: `${progressPercent}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {paidCount} of {plan.installmentCount} · {progressPercent}% complete
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatCurrency(plan.startingBalance)}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {formatDate(plan.startDate)}
                                    </td>
                                    <td className="px-4 py-3 text-sm dark:text-gray-300">
                                        {endDateDisplay}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            {showFooter && (
                <div className="pt-3 pb-0 bg-gray-50 dark:bg-slate-800 flex items-center justify-between gap-3">
                    <div>
                        {inactivePlans.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowInactive((v) => !v)}
                                className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-700 dark:hover:text-gray-200"
                            >
                                {showInactive
                                    ? `Hide ${inactivePlans.length} inactive plan${inactivePlans.length > 1 ? 's' : ''}`
                                    : `Show ${inactivePlans.length} inactive plan${inactivePlans.length > 1 ? 's' : ''}`}
                            </button>
                        )}
                    </div>
                    {footerAction != null && <div className="ml-auto">{footerAction}</div>}
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const TabIncentivePlans: React.FC<TabIncentivePlansProps> = ({
  data,
  tenantAccountId,
  incentivePlanOptions,
    lastKingInvoice,
    utilityName,
    onRefresh,
}) => {
    const { permissions } = useAppSelector((state) => state.app)
  const dispatch = useAppDispatch()

    const [currentView, setCurrentView] = useState<BillingView>({ view: 'overview' })

    const refreshAndGoBack = async () => {
        await onRefresh?.()
        setCurrentView({ view: 'overview' })
    }
    const refreshInPlace = async () => {
        await onRefresh?.()
    }
  const [kingOnlyBalance, setKingOnlyBalance] = useState<number | undefined>(undefined)
  const [estimatedUtilityBalance, setEstimatedUtilityBalance] = useState<number | undefined>(undefined)
  const [isKingBalanceInflated, setIsKingBalanceInflated] = useState<boolean>(false)
    const [showInactiveCredits, setShowInactiveCredits] = useState(false)

    // Permissions
    const hasCreateCorpPlanPermission =
        permissions?.includes('incentive-plan:corp:create') ?? false
    const hasCancelCorpPlanPermission =
        permissions?.includes('incentive-plan:corp:cancel') ?? false
    const hasCreateUtilityPlanPermission =
        permissions?.includes('incentive-plan:utility:create') ?? false
    const hasCancelUtilityPlanPermission =
        permissions?.includes('incentive-plan:utility:cancel') ?? false
    const hasCreateAdjPermission =
        permissions?.includes('incentive-plan:adj:create') ?? false
    const hasCancelAdjPermission =
        permissions?.includes('incentive-plan:adj:cancel') ?? false
    const hasReverseAdjPermission =
        permissions?.includes('incentive-plan:adj:reverse') ?? false

    // Fetch balances
  useEffect(() => {
      const fetchBalances = async (): Promise<BillingSdkTenantAccountBalances | undefined> => {
      try {
          return await getTenantAccountBalancesFromBillingSdkApi(tenantAccountId)
      } catch (e: any) {
          dispatch(
          pushToast({
              message: 'Failed to get account balances',
            type: 'error',
            description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
          })
        )
      }
    }

      fetchBalances()
      .then((balances) => {
        if (balances != null) {
          setKingOnlyBalance(Number(balances.kingOnlyBalance))
          setEstimatedUtilityBalance(Number(balances.estimatedUtilityBalance))
          setIsKingBalanceInflated(balances.isKingBalanceInflated ?? false)
        }
      })
      .catch(() => {})
  }, [dispatch, tenantAccountId])

    // Partition data
    const sortByCreatedAtDesc = (a: IncentivePlan, b: IncentivePlan) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()

    const kingPaymentPlans = data.tenantAccountIncentivePlans.filter(isPlanType).slice().sort(sortByCreatedAtDesc)
    const kingCredits = data.tenantAccountIncentivePlans.filter(isCreditType).slice().sort(sortByCreatedAtDesc)
    const utilityPaymentPlans = data.utilityAccountIncentivePlans.filter(isPlanType).slice().sort(sortByCreatedAtDesc)
    const utilityCredits = data.utilityAccountIncentivePlans.filter(isCreditType).slice().sort(sortByCreatedAtDesc)

    // All accommodation credits (king + utility) in one table for the overview
    const allCredits: Array<{ credit: IncentivePlan; accountType: 'King' | 'Utility' }> = [
        ...kingCredits.map((c) => ({ credit: c, accountType: 'King' as const })),
        ...utilityCredits.map((c) => ({ credit: c, accountType: 'Utility' as const })),
    ]
    const activeCredits = allCredits.filter(({ credit }) => {
        if (credit.status === 'ACTIVE') return true
        if (credit.status === 'CREDITED') {
            return credit.installments.some((i) => i.status === 'PENDING' || i.status === 'SCHEDULED')
        }
        return false
    })
    const inactiveCredits = allCredits.filter(({ credit }) => !activeCredits.some((a) => a.credit.id === credit.id))
    const visibleCredits = showInactiveCredits ? allCredits : activeCredits

    const navigateTo = (view: BillingView) => setCurrentView(view)
    const goBack = () => setCurrentView({ view: 'overview' })

    // ---------------------------------------------------------------------------
    // Sub-view: create payment plan
    // ---------------------------------------------------------------------------
    if (currentView.view === 'create-payment-plan') {
    return (
        <div className="py-4">
            <CreatePaymentPlanForm
                tenantAccountId={tenantAccountId}
                incentivePlanOptions={incentivePlanOptions}
                kingOnlyBalance={kingOnlyBalance}
                isKingBalanceInflated={isKingBalanceInflated}
                hasCreateKingPlanPermission={hasCreateCorpPlanPermission}
                lastKingInvoice={lastKingInvoice}
                onSuccess={refreshAndGoBack}
                onCancel={goBack}
            />
        </div>
        )
    }

    // ---------------------------------------------------------------------------
    // Sub-view: create utility payment plan
    // ---------------------------------------------------------------------------
    if (currentView.view === 'create-utility-payment-plan') {
        return (
            <div className="py-4">
                <CreateUtilityPaymentPlanForm
                    tenantAccountId={tenantAccountId}
                    estimatedUtilityBalance={estimatedUtilityBalance}
                    hasCreateUtilityPlanPermission={hasCreateUtilityPlanPermission}
                    utilityName={utilityName ?? 'Utility'}
                    lastKingInvoice={lastKingInvoice}
                    onSuccess={refreshAndGoBack}
                    onCancel={goBack}
                />
            </div>
        )
    }

    // ---------------------------------------------------------------------------
    // Sub-view: payment plan detail
    // ---------------------------------------------------------------------------
    if (currentView.view === 'payment-plan-detail') {
        const allPlans: IncentivePlan[] = [
            ...data.tenantAccountIncentivePlans,
            ...data.utilityAccountIncentivePlans,
        ]
        const plan = allPlans.find((p) => p.id === currentView.planId)

        if (!plan) {
            return (
                <div className="py-4">
                    <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-400">
                            Plan <strong>{currentView.planId}</strong> not found.
                        </p>
                    </div>
          </div>
            )
        }

        return (
            <div className="py-4">
                <PaymentPlanDetail
                    plan={plan}
                    planAccountType={currentView.accountType}
                    utilityName={utilityName}
                    hasCancelPermission={currentView.accountType === 'King' ? hasCancelCorpPlanPermission : hasCancelUtilityPlanPermission}
                    onBack={goBack}
                    onCancelled={refreshInPlace}
                    tenantAccountId={tenantAccountId}
                />
          </div>
        )
    }

    // ---------------------------------------------------------------------------
    // Sub-view: create credit
    // ---------------------------------------------------------------------------
    if (currentView.view === 'create-credit') {
        return (
            <div className="py-4">
                <CreateCreditForm
                    tenantAccountId={tenantAccountId}
                    estimatedUtilityBalance={estimatedUtilityBalance}
                    kingOnlyBalance={kingOnlyBalance}
                    hasCreateKingCreditPermission={hasCreateAdjPermission}
                    hasCreateUtilityCreditPermission={hasCreateAdjPermission}
                    lastKingInvoice={lastKingInvoice}
                    onSuccess={refreshAndGoBack}
                    onCancel={goBack}
                />
            </div>
        )
    }

    // ---------------------------------------------------------------------------
    // Sub-view: credit detail
    // ---------------------------------------------------------------------------
    if (currentView.view === 'credit-detail') {
        const allCreditsFlat: IncentivePlan[] = [
            ...data.tenantAccountIncentivePlans,
            ...data.utilityAccountIncentivePlans,
        ]
        const credit = allCreditsFlat.find((c) => c.id === currentView.creditId)

        if (!credit) {
            return (
                <div className="py-4">
                    <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-400">
                            Credit <strong>{currentView.creditId}</strong> not found.
                        </p>
                    </div>
                </div>
            )
        }

        return (
            <div className="py-4">
                <CreditDetail
                    credit={credit}
                    creditAccountType={currentView.accountType}
                    hasCancelPermission={hasCancelAdjPermission}
                    hasReversePermission={hasReverseAdjPermission}
                    onBack={goBack}
                    onReversed={refreshInPlace}
                    tenantAccountId={tenantAccountId}
                />
            </div>
    )
  }

    // ---------------------------------------------------------------------------
    // Overview
    // ---------------------------------------------------------------------------
    return (
        <div className="min-w-full w-full">
            <div className="grid min-w-full w-full grid-cols-1 gap-6 py-4">
                {/* ------------------------------------------------------------------ */}
                {/* King Energy Payment Plans                                           */}
                {/* ------------------------------------------------------------------ */}
                <Panel
                    header={
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-7 h-7 rounded-md bg-purple-100 dark:bg-purple-900/40">
                                <BoltIcon className="w-4 h-4 text-purple-700 dark:text-purple-400" />
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">King Energy Payment Plans</span>
                                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                    King Energy-sponsored structured payment arrangements and balance deferrals
                                </span>
                            </div>
                        </div>
                    }
                >
                    <PlanTable
                        plans={kingPaymentPlans}
                        accountType="King"
                        emptyLabel="King Energy"
                        onRowClick={(plan) =>
                            navigateTo({ view: 'payment-plan-detail', planId: plan.id, accountType: 'King' })
                        }
                        footerAction={
                            hasCreateCorpPlanPermission ? (
                                kingPaymentPlans.some((p) => p.status === 'ACTIVE') ? (
                                    <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                        <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                        You cannot create a new King Energy payment plan when one is active.
                                    </span>
                                ) : (
                                    <Button color="indigo" onClick={() => navigateTo({ view: 'create-payment-plan' })}>
                                        Add King Energy Payment Plan
                                    </Button>
                                )
                            ) : (
                                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                        <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                        Sufficient permission required to create a King Energy payment plan.
                                </span>
                            )
                      }
                    />
                </Panel>

                {/* ------------------------------------------------------------------ */}
                {/* Utility Payment Plans                                               */}
                {/* ------------------------------------------------------------------ */}
                <Panel
                    header={
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-7 h-7 rounded-md bg-orange-100 dark:bg-orange-900/40">
                                <BuildingOffice2Icon className="w-4 h-4 text-orange-700 dark:text-orange-400" />
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{utilityName ?? 'Utility'} Payment Plans</span>
                                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                    {utilityName ?? 'Utility'}-sponsored payment arrangements (externally managed)
                                </span>
                            </div>
                        </div>
                  }
                >
                    <PlanTable
                        plans={utilityPaymentPlans}
                        accountType="Utility"
                        emptyLabel={utilityName ?? 'utility'}
                        onRowClick={(plan) =>
                            navigateTo({ view: 'payment-plan-detail', planId: plan.id, accountType: 'Utility' })
                        }
                        footerAction={
                            hasCreateUtilityPlanPermission ? (
                                utilityPaymentPlans.some((p) => p.status === 'ACTIVE') ? (
                                    <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                        <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                        You cannot create a new {utilityName ?? 'Utility'} payment plan when one is active.
                                    </span>
                                ) : (
                                    <Button color="indigo" onClick={() => navigateTo({ view: 'create-utility-payment-plan' })}>
                                        Add {utilityName ?? 'Utility'} Payment Plan
                                    </Button>
                                )
                            ) : (
                                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                        <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                        Sufficient permission required to create an externally managed payment plan.
                                </span>
                            )
                        }
                    />
                </Panel>

                {/* ------------------------------------------------------------------ */}
                {/* Accommodation Credits                                               */}
                {/* ------------------------------------------------------------------ */}
                <Panel
                    header={
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-7 h-7 rounded-md bg-emerald-100 dark:bg-emerald-900/40">
                                <GiftIcon className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                            </div>
                            <div>
                                <span className="font-semibold text-gray-900 dark:text-gray-100">Accommodation Credits</span>
                                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                    Credits for service interruptions and other accommodations
                                </span>
                            </div>
                        </div>
                  }
                >
                    {allCredits.length === 0 ? (
                        <div>
                            <div className="py-2.5">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    No active accommodation credits found for this account
                                </p>
                            </div>
                            <div className="flex items-center justify-end py-3 dark:bg-slate-800">
                                {hasCreateAdjPermission ? (
                                    <Button color="indigo" onClick={() => navigateTo({ view: 'create-credit' })}>
                                        Add Accommodation Credit
                                    </Button>
                                ) : (
                                    <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                            <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                        Sufficient permission required to create accommodation credits.
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {visibleCredits.length === 0 && (
                                <div className="py-2.5">
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No active accommodation credits</p>
                                    </div>
                                )}
                                <div className={visibleCredits.length === 0 ? 'hidden' : 'overflow-x-auto'}>
                                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                                        <thead className="bg-white dark:bg-slate-950">
                                            <tr>
                                                {['Adjustment ID', 'Status', 'Credits', 'Cadence', 'Amount', 'Remaining', 'Created Date', 'Description'].map((h) => (
                                                    <th key={h} className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider dark:text-white">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                            {visibleCredits.map(({ credit, accountType }) => {
                                                const appliedCount = credit.installments.filter((i) => ['CREDITED', 'BILLED', 'COMPLETED'].includes(i.status)).length
                                                const progressPercent =
                                                    credit.installmentCount > 0 ? Math.round((appliedCount / credit.installmentCount) * 100) : 0
                                                const hasPending = credit.installments.some(
                                                    (i) => i.status === 'PENDING' || i.status === 'SCHEDULED'
                                                )
                                                const displayStatus =
                                                    credit.status === 'CREDITED' && hasPending
                                                        ? 'PARTIALLY_CREDITED'
                                                        : credit.status
                                                return (
                                                    <tr
                                                        key={credit.id}
                                                        className="bg-slate-50 dark:bg-slate-950/50 cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                                                        onClick={() =>
                                                            navigateTo({ view: 'credit-detail', creditId: credit.id, accountType })
                                                        }
                                                    >
                                                        <td className="px-4 py-3 text-sm font-mono dark:text-gray-300">{credit.humanId ?? credit.id}</td>
                                                        <td className="px-4 py-3">
                                                            <Badge status={displayStatus} />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="min-w-[4rem] space-y-1">
                                                                <div className="h-1.5 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-green-500 dark:bg-green-400 transition-all"
                                                                        style={{ width: `${progressPercent}%` }}
                                                                    />
                                                                </div>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {appliedCount} of {credit.installmentCount} · {progressPercent}% complete
                                                                </p>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm dark:text-gray-300">
                                                            {credit.cadence === 'ONE_TIME'
                                                                ? 'One-time'
                                                                : credit.installmentCount > 1
                                                                    ? `${credit.installmentCount} mo`
                                                                    : credit.cadence?.replaceAll('_', ' ')}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 font-medium">
                                                            {formatCurrency(credit.startingBalance)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm dark:text-gray-300">
                                                            {formatCurrency(credit.remainingBalance)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm dark:text-gray-300">
                                                            {credit.createdAt ? formatDateTime(new Date(credit.createdAt)) : '\u2014'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm dark:text-gray-300 max-w-xs">
                                                            <p className="truncate">{credit.memo ?? '\u2014'}</p>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pt-3 pb-0 bg-gray-50 dark:bg-slate-800 flex items-center justify-between gap-3">
                                <div>
                                    {inactiveCredits.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowInactiveCredits((v) => !v)}
                                            className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-700 dark:hover:text-gray-200"
                                        >
                                            {showInactiveCredits
                                                ? `Hide ${inactiveCredits.length} inactive credit${inactiveCredits.length > 1 ? 's' : ''}`
                                                : `Show ${inactiveCredits.length} inactive credit${inactiveCredits.length > 1 ? 's' : ''}`}
                                        </button>
                                    )}
                                </div>
                                <div className="ml-auto">
                                    {hasCreateAdjPermission ? (
                                        <Button color="indigo" onClick={() => navigateTo({ view: 'create-credit' })}>
                                            Add Accommodation Credit
                                        </Button>
                                    ) : (
                                        <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                                    <InformationCircleIcon className="w-4 h-4 shrink-0" />
                                            Sufficient permission required to create accommodation credits.
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Panel>
            </div>
        </div>
  )
}

export default TabIncentivePlans
